// ============================================================================
// FSA DOCUMENT OUTPUT CONFIGURATION — TypeScript types (ADR-0003)
// ============================================================================
// Mirrors the C# DocumentOutputConfig / DocumentOutputProfileDto. The persisted
// "house style" that drives the FSA PDF deliverable (header content, page size,
// font scale, footer, board signature preamble).

import type { ID } from '../common.types';

export type PageSizeKey = 'A4' | 'LEGAL' | 'LETTER';
export type FontScale = 'SMALL' | 'MEDIUM' | 'LARGE' | 'XLARGE';
export type HeaderAlign = 'LEFT' | 'CENTER' | 'RIGHT';
export type PageNumberPosition = 'LEFT' | 'CENTER' | 'RIGHT';
export type HeaderFieldKey = 'CLIENT_LEGAL_NAME' | 'ADDRESS' | 'FINANCIAL_YEAR' | 'REGISTRATION';
export type ColorMode = 'MONOCHROME' | 'BRAND';
export type BodyAlign = 'LEFT' | 'JUSTIFY';

export interface HeaderFieldConfig {
  key: HeaderFieldKey;
  /** For key = REGISTRATION — the basic.tbl_registration_type.code (CIN / GSTIN / …). */
  registrationTypeCode?: string | null;
  enabled: boolean;
}

export type HeaderRule = 'NONE' | 'SINGLE' | 'DOUBLE';

export interface HeaderConfig {
  align: HeaderAlign;
  fields: HeaderFieldConfig[];
  /** Rule under the header block. Absent on older profiles — treated as SINGLE. */
  rule?: HeaderRule;
}

export const HEADER_RULE_LABELS: Record<HeaderRule, string> = {
  NONE: 'None',
  SINGLE: 'Single line',
  DOUBLE: 'Double line (thick and thin)',
};

/** PRINTER_SAFE = 8 mm bottom margin (footer clear of the printer's no-print strip); COMPACT = 3.5 mm. */
export type PageMarginsKey = 'PRINTER_SAFE' | 'COMPACT';
/** `margins` absent = a profile saved before the setting → COMPACT (unchanged pagination). */
export interface PageConfig { size: PageSizeKey; margins?: PageMarginsKey; }
export interface FontConfig { scale: FontScale; }
export interface PageNumberConfig { show: boolean; position: PageNumberPosition; }
export interface FooterConfig { customText?: string | null; pageNumber: PageNumberConfig; }
/** ADR-0003 Phase 1.5 — print palette. MONOCHROME is the statutory deliverable. */
export interface ColorConfig { mode: ColorMode; }
/**
 * ADR-0024 — body-text house style: alignment of narrative paragraphs and list
 * items (policies, reports, AGM notice). A paragraph's own editor alignment
 * overrides it; table cells, headings and signatures are never affected.
 */
export interface TextConfig { bodyAlign: BodyAlign; }

export type LetterheadStyle = 'CLASSIC' | 'MODERN' | 'PREPRINTED' | 'NONE';
/**
 * ADR-0024 A-2 — letterhead house style for the auditor's documents (Auditor's
 * Report, CARO, IFC annexure). The firm's details come from the audit firm
 * master; auditor documents follow the global page size, font, body alignment
 * and page numbers, and replace the client header / footer text.
 */
export interface AuditorDocsConfig {
  letterheadStyle: LetterheadStyle;
  /** FIRST — full letterhead on page 1, compact name after · EVERY — full on every page. */
  letterheadPages: 'FIRST' | 'EVERY';
  showLogo: boolean;
  /** MONOCHROME, or FIRM — the firm master's accent colour for the name and rule. */
  letterheadColor: 'MONOCHROME' | 'FIRM';
  /** Address · GSTIN · FRN line in the footer. */
  showFirmFooter: boolean;
  /** Where the partner's UDIN prints: the Auditor's Report / CARO and the statements' signing block (default), or the reports only. */
  udinOn?: 'REPORT_AND_STATEMENTS' | 'REPORT_ONLY';
}

export interface DocumentOutputConfig {
  version: number;
  header: HeaderConfig;
  page: PageConfig;
  font: FontConfig;
  footer: FooterConfig;
  color: ColorConfig;
  /** Absent on profiles saved before ADR-0024 — treated as LEFT. */
  text?: TextConfig;
  /** ADR-0024 A-2 — absent on older profiles: the defaults apply (Classic, first page, logo, monochrome, footer). */
  auditorDocs?: AuditorDocsConfig;
}

export const DEFAULT_AUDITOR_DOCS: AuditorDocsConfig = {
  letterheadStyle: 'CLASSIC',
  letterheadPages: 'FIRST',
  showLogo: true,
  letterheadColor: 'MONOCHROME',
  showFirmFooter: true,
  udinOn: 'REPORT_AND_STATEMENTS',
};

export const LETTERHEAD_STYLE_LABELS: Record<LetterheadStyle, string> = {
  CLASSIC: 'Classic — centred name, double rule',
  MODERN: 'Modern — name left, contacts right',
  PREPRINTED: 'Pre-printed stationery (leave space)',
  NONE: 'None — no letterhead',
};

export interface DocumentOutputProfile {
  id: ID;
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusId?: string | null;
  statusCode?: string;
  statusName?: string;
  statusColor?: string | null;
  rowVersion: number;
}

export interface CreateDocumentOutputProfileRequest {
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusCode?: string;
}

export interface UpdateDocumentOutputProfileRequest {
  name: string;
  description?: string | null;
  isDefault: boolean;
  config: DocumentOutputConfig;
  statusCode?: string;
  rowVersion: number;
}

export interface RegistrationTypeOption { code: string; name: string; }

/** Mirrors C# DocumentOutputConfig.Default(). */
export const DEFAULT_OUTPUT_CONFIG: DocumentOutputConfig = {
  version: 1,
  header: {
    align: 'CENTER',
    rule: 'SINGLE',
    fields: [
      { key: 'CLIENT_LEGAL_NAME', enabled: true },
      { key: 'ADDRESS', enabled: true },
      { key: 'FINANCIAL_YEAR', enabled: true },
    ],
  },
  page: { size: 'A4', margins: 'PRINTER_SAFE' },
  font: { scale: 'MEDIUM' },
  footer: { customText: null, pageNumber: { show: true, position: 'RIGHT' } },
  color: { mode: 'MONOCHROME' },
  text: { bodyAlign: 'LEFT' },
  auditorDocs: { letterheadStyle: 'CLASSIC', letterheadPages: 'FIRST', showLogo: true, letterheadColor: 'MONOCHROME', showFirmFooter: true },
};

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  SMALL: 'Small', MEDIUM: 'Medium', LARGE: 'Large', XLARGE: 'Extra Large',
};

export const COLOR_MODE_LABELS: Record<ColorMode, string> = {
  MONOCHROME: 'Monochrome (final statement)',
  BRAND: 'Brand colour (drafts)',
};

export const BODY_ALIGN_LABELS: Record<BodyAlign, string> = {
  LEFT: 'Left aligned',
  JUSTIFY: 'Justified',
};
